import React, { Dispatch, ReactNode, SetStateAction, createContext, useCallback, useContext, useEffect, useState } from "react";

import { getUserData, logMixpanelId, verifyUser } from "services";

import {
  changeLanguage,
  getFarmIdFromLocalStorage,
  getLocalKlimVar,
  getLocalStorageCropExplorerPseudoUserId,
  mapAppTypeToString,
  setLocalKlimVar,
  setLocalStorageCropExplorerDismissedOnboardingTour,
  setLocalStorageCropExplorerOnboarded,
  useAppType,
  useCacheInvalidation,
  useCancelAsyncOnUnmount,
  useCurrentYearlyDataYear,
  useLocation,
  useNavigate,
  useNetworkState,
  useTranslation,
} from "utils";

import { User } from "models";

import { Message as message } from "components";

import { Verifying } from "pages/app/account/Verifying";

export type UserContextType = {
  user: User | null;
  setUser: Dispatch<SetStateAction<User | null>>;
  setTermsOverlayHidden: Dispatch<SetStateAction<boolean>>;
  termsOverlayHidden: boolean;
  reloadUserData: () => void;
  verifying: boolean;
  switchFarm: (farmId: string | null) => void;
};

// create user context provider
const UserContext = createContext<UserContextType>({
  user: null,
  setUser: () => null,
  reloadUserData: () => null,
  verifying: true,
  switchFarm: () => null,
  setTermsOverlayHidden: () => null,
  termsOverlayHidden: false,
});

// set paths that should be available for non-signed in users
const noRedirect = ["/t/", "/sign-in", "/sign-up", "/confirm-account", "/password", "/sign-out", "/update-app", "/page"];

export const UserContextProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const appType = useAppType();
  const network = useNetworkState();
  const signal = useCancelAsyncOnUnmount()();

  const [currentLocation, setCurrentLocation] = useState({
    path: location.pathname,
    search: location.search,
  });
  const [termsOverlayHidden, setTermsOverlayHidden] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [verifying, setVerifying] = useState(!!getLocalKlimVar("token"));
  const { year: currentYearlyDataYear, refetch } = useCurrentYearlyDataYear(user?.farm?.id || "");

  useCacheInvalidation(!!user);

  const shouldRedirectToProfileRoutes = !noRedirect.some((v) => location.pathname.includes(v));

  // redirect to sign in or content pages if user not logged in
  const goToSigninMaybe = useCallback(() => {
    if (location.pathname.includes("/news") || location.pathname.includes("/article") || location.pathname.includes("/community")) {
      navigate(location.pathname.replace("", ""));
    } else if (shouldRedirectToProfileRoutes) {
      setVerifying(false);
      // setLocalKlimVar("urlSlice", location.pathname.split(process.env.REACT_APP_FARM_URL)[1]); // this probably needs some other logic, disabling for now
      navigate("/sign-in");
    }
  }, [navigate, location, shouldRedirectToProfileRoutes]);

  const switchFarm = useCallback((farmId: string | null) => {
    setLocalKlimVar("farmId", farmId);
  }, []);

  useEffect(() => {
    if (user?.id && user.farm?.id) {
      refetch();
    }
  }, [currentYearlyDataYear, refetch, user?.id, user?.farm?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // if user cannot be verified, continue to redirection
  const clearUser = useCallback(() => {
    if (getLocalKlimVar("token")) {
      message({ content: t("errors.unverified"), icon: "error" });
    }

    setUser(null);
    setVerifying(false);
    setLocalKlimVar("token", null);

    // Reset farm id, because if a different user tries to sign in, the old farm id
    // would be used leading to the backend returning 401
    switchFarm(null);
    goToSigninMaybe();
  }, [t, switchFarm, goToSigninMaybe]);

  // set user details after data collection
  const setLocalStorage = useCallback(
    async (details: User) => {
      if (details?.language) {
        const languageFromLocalStorage = getLocalKlimVar("language");
        if (languageFromLocalStorage !== details.language) {
          await changeLanguage(details.language, user);
        }
      }

      const countryFromLocalStorage = getLocalKlimVar("country");
      if (countryFromLocalStorage !== details?.farm?.country) {
        setLocalKlimVar("country", details?.farm?.country);
      }

      if (details?.farm?.id) {
        const farmIdFromLocalStorage = getFarmIdFromLocalStorage();

        if (farmIdFromLocalStorage !== details.farm.id) {
          switchFarm(details.farm.id);
        }
      }

      // override cropExplorer keys in localStorage only if the user is not migrating data form a logged out account,
      // otherwise keep the local state for onboarding and tour
      if (details?.cropExplorer && !getLocalStorageCropExplorerPseudoUserId()) {
        setLocalStorageCropExplorerDismissedOnboardingTour(!!details.cropExplorer?.cropExplorerDismissedOnboardingTour);
        setLocalStorageCropExplorerOnboarded(!!details.cropExplorer?.cropExplorerOnboarded);
      }
    },
    [user, switchFarm]
  );

  // update user's mixpanel id if needed
  const updateMixpanelId = useCallback(
    (mixpanelId: string) => {
      logMixpanelId({ id: mixpanelId, signal });
    },
    [signal]
  );

  // get user data
  const loadUser = useCallback(() => {
    getUserData({
      appType: mapAppTypeToString(appType),
      signal,
    })
      .then((res) => {
        setUser(res);
        setLocalStorage(res);

        if (getLocalKlimVar("sessionId")) {
          updateMixpanelId(getLocalKlimVar("sessionId"));
        }

        setTimeout(() => setVerifying(false), 400); // min verification time so we don't have a flash in/out splash page

        if (shouldRedirectToProfileRoutes && !location.pathname.includes("")) {
          navigate(`${location.pathname ? location.pathname : "/home"}${location.search}`);
        } else if (!shouldRedirectToProfileRoutes) {
          navigate("/home");
        }
      })
      .catch((_err) => {
        clearUser();
      });
  }, [appType, clearUser, signal, navigate, setLocalStorage, updateMixpanelId, shouldRedirectToProfileRoutes, location.pathname, location.search]);

  // update user data from child pages
  const reloadUserData = useCallback(
    () =>
      getUserData({
        appType: mapAppTypeToString(appType),
        signal,
      }).then((res) => {
        setUser(res);
        setLocalStorage(res);
      }),
    [appType, signal, setLocalStorage]
  );

  // verify user
  const verifyUserSession = () => {
    const token = getLocalKlimVar("token");
    if (network && token && token !== "null" && !location.pathname.includes("/t/") && !location.pathname.includes("/sign-out/")) {
      verifyUser({
        appType: mapAppTypeToString(appType),
        signal,
      })
        .then((res) => {
          if (res.verified) {
            setLocalKlimVar("token", res.token);
            loadUser();
          } else {
            clearUser();
          }
        })
        .catch((_err) => {
          clearUser();
        });
    } else if (network && location.pathname.includes("/") && !location.pathname.includes("/t/")) {
      clearUser();
    } else if (network && !location.pathname.includes("/t/")) {
      setUser(null);
      setVerifying(false);
    } else if (location.pathname.includes("/t/")) {
      setVerifying(false);
    }
  };

  useEffect(() => {
    // Remove param to avoid side effects if user switches farm later
    const params = new URLSearchParams(currentLocation.search);
    const farmId = params.get("farmId");

    if (farmId) {
      params.delete("farmId");
      navigate({
        search: params.toString(),
      });

      switchFarm(farmId);
    }

    // verify user
    verifyUserSession();
  }, [currentLocation.path, switchFarm]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (location.pathname !== currentLocation.path || location.search !== currentLocation.search) {
      setCurrentLocation({
        path: location.pathname,
        search: location.search,
      });
    }
  }, [currentLocation, location]);

  useEffect(() => {
    const params = location.search.replace("?", "");
    if (params !== "") {
      const paramsArray = params.split("&");
      paramsArray.forEach((item) => {
        setLocalKlimVar(item.split("=")[0], item.split("=")[1]);
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (currentLocation.path === "/sign-out") {
      setTermsOverlayHidden(false);
    }
  }, [currentLocation.path]);

  return (
    <UserContext.Provider
      value={{
        user,
        reloadUserData,
        setTermsOverlayHidden,
        setUser,
        termsOverlayHidden,
        verifying,
        switchFarm,
      }}
    >
      {verifying ? <Verifying /> : children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => useContext(UserContext);

export const UserContextConsumer = UserContext.Consumer;
